import { I18n } from "@assets/strings";
import moment from "moment-timezone";
import React, { ComponentProps } from "react";
import DatePicker from "react-datepicker";
import styled from "styled-components";
import { Row } from "@ui/components/layout";

export type DatePickerTypes = "HOURS_RANGE";

interface AdvancedDatePickerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
	timezone?: string;
	type: DatePickerTypes;
	local?: string;
	customInput?: React.ReactElement;
	customInputHStart?: React.ReactElement;
	customInputHEnd?: React.ReactElement;
	onChangeStart?: (date: Date | null) => void;
	onChangeEnd?: (date: Date | null) => void;
	startDate?: Date;
	endDate?: Date;
}

const regulatedOnChange = (params: { cb?: (v: Date | null) => void; timezone: string }) => {
	return (date: Date | null) => {
		const now = new Date();
		const clampedDate = date && date >= now ? date : now;
		return params.cb?.(clampedDate ? setOtherZone(clampedDate, params.timezone) : null);
	};
};

export const AdvancedDatePicker: React.FC<
	AdvancedDatePickerProps & Omit<ComponentProps<typeof DatePicker>, "onChange">
> = ({
	timezone = moment.tz.guess(),
	customInput,
	local = "fr",
	onChangeStart,
	onChangeEnd,
	customInputHStart,
	customInputHEnd,
	startDate,
	type,
	endDate,
	maxTime,
	minDate,
	minTime,
}) => {
	return type === "HOURS_RANGE" ? (
		<Row>
			<Bloc>
				<DatePicker
					locale={local}
					minDate={minDate}
					selected={startDate ? setLocalZone(startDate, timezone) : null}
					onChange={regulatedOnChange({ cb: onChangeStart, timezone })}
					customInput={customInput ? customInput : undefined}
				/>
			</Bloc>
			<Bloc style={{ marginTop: 33, marginLeft: 13, marginRight: 13 }}>{I18n.t("live.from")}</Bloc>
			<Bloc>
				<DatePicker
					minDate={minDate}
					locale={local}
					dropdownMode="select"
					showTimeSelectOnly={true}
					showTimeSelect
					dateFormat="HH:mm"
					selected={startDate ? setLocalZone(startDate, timezone) : null}
					timeIntervals={15}
					onChange={regulatedOnChange({ cb: onChangeStart, timezone })}
					customInput={customInputHStart ? customInputHStart : undefined}
				/>
			</Bloc>
			<Bloc style={{ marginTop: 33, marginLeft: 13, marginRight: 13 }}>{I18n.t("live.to")}</Bloc>
			<Bloc>
				<DatePicker
					minDate={minDate}
					readOnly={!onChangeEnd}
					locale={local}
					showTimeSelectOnly={true}
					showTimeSelect
					dateFormat="HH:mm"
					minTime={minTime}
					timeIntervals={15}
					maxTime={maxTime}
					selected={endDate ? setLocalZone(endDate, timezone) : null}
					onChange={regulatedOnChange({ cb: onChangeEnd, timezone })}
					customInput={customInputHEnd ? customInputHEnd : undefined}
				/>
			</Bloc>
		</Row>
	) : (
		<></>
	);
};

const setLocalZone = (date: Date, timezone: string): Date => {
	const dateWithoutZone = moment.tz(date, timezone).format("YYYY-MM-DDTHH:mm:ss.SSS");
	const localZone = moment(dateWithoutZone).format("Z");
	const dateWithLocalZone = [dateWithoutZone, localZone].join("");

	return new Date(dateWithLocalZone);
};

const setOtherZone = (date: Date, timezone: string): Date => {
	const dateWithoutZone = moment(date).format("YYYY-MM-DDTHH:mm:ss.SSS");
	const otherZone = moment.tz(date, timezone).format("Z");
	const dateWithOtherZone = [dateWithoutZone, otherZone].join("");

	return new Date(dateWithOtherZone);
};

const Bloc = styled.div`
	position: relative;
	cursor: pointer;
	font-family: Sora;
	margin-top: 12px;
	${({ theme }) => theme.colors.primary};
	font-size: 13px;
	font-weight: 100;
`;
